import React from 'react';
import { WRAP, SECTION_PAD } from '../styles/layout';
import { Helmet } from 'react-helmet-async';

/* The privacy policy of the RevSec AI Guardian browser extension, linked from
   its Chrome Web Store listing. Its text is docs/PRIVACY_POLICY.md in the
   RevUp-AI/revsec-browser-extension repo: change both together. */

const BG  = '#09060F';
const C   = '#00D4AA';
const R   = '0,212,170';
const LAV = '#EDE9F8';
const LAV2= '#C8C0EC';
const LAV3= '#9B90BA';

const H2 = ({ children }) => (
  <h2 style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:800, fontSize:'clamp(20px,1.8vw,26px)', color:LAV, margin:'40px 0 14px', letterSpacing:'-0.5px', lineHeight:1.2 }}>{children}</h2>
);
const P = ({ children, style={} }) => (
  <p style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:15, fontWeight:300, lineHeight:1.85, color:LAV3, margin:'0 0 16px', ...style }}>{children}</p>
);
const Li = ({ children }) => (
  <li style={{ fontFamily:"'DM Sans',system-ui,sans-serif", fontSize:15, fontWeight:300, lineHeight:1.85, color:LAV3, margin:'0 0 8px' }}>{children}</li>
);
const B = ({ children }) => <strong style={{ color:LAV2 }}>{children}</strong>;

export default function PrivacyRevSecAIGuardian() {
  return (
    <>
      <Helmet>
        <title>RevSec AI Guardian Privacy Policy — RevUpAI</title>
        <meta name="description" content="Privacy policy of the RevSec AI Guardian browser extension: what it handles, where it sends it, and what stays in your browser." />
        <link rel="canonical" href="https://revupai.com/privacy/revsec-ai-guardian" />
        <meta name="robots" content="noindex" />
      </Helmet>
    <div style={{ background:BG, minHeight:'100vh', paddingTop:80 }}>
      <div style={{ ...WRAP, maxWidth:780, padding:`${SECTION_PAD} clamp(20px,4vw,40px)` }}>

        {/* Header */}
        <div style={{ marginBottom:48, paddingBottom:32, borderBottom:`1px solid rgba(${R},.15)` }}>
          <div style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'5px 14px', borderRadius:100, background:`rgba(${R},.08)`, border:`1px solid rgba(${R},.2)`, marginBottom:24 }}>
            <span style={{ fontSize:11, fontWeight:700, letterSpacing:2.5, textTransform:'uppercase', color:C }}>Legal</span>
          </div>
          <h1 style={{ fontFamily:"'Fraunces',Georgia,serif", fontWeight:900, fontSize:'clamp(32px,3.5vw,52px)', color:LAV, margin:'0 0 16px', letterSpacing:'-1.5px', lineHeight:1.08 }}>RevSec AI Guardian: privacy policy</h1>
          <P style={{ color:LAV2 }}>Last updated: 16 October 2026</P>
          <P>RevSec AI Guardian ("the extension") is a browser extension published by RevUp AI LLC for organizations that use RevSec, an AI security platform. An organization's IT team installs and configures it on the browsers it manages. This policy describes what the extension handles and where it sends it.</P>
        </div>

        <H2>1. What the extension handles</H2>
        <P>The extension works only on supported AI assistant pages: claude.ai, GitHub Copilot's chat at github.com/copilot and in the same Copilot box on GitHub's home page, and grok.com. On those pages, when you send a message, it handles:</P>
        <ul style={{ margin:'0 0 16px', paddingLeft:24 }}>
          <Li><B>The message you are sending.</B> Its text is sent to your organization's RevSec service to be checked against your organization's policy before it is sent to the AI assistant.</Li>
          <Li><B>The signed-in account.</B> The email address, or GitHub username, of the account signed into the AI assistant, and for Claude the Claude organization and plan type. This applies your organization's account policy (for example, company accounts only) and attributes its records.</Li>
          <Li><B>Technical details of the check.</B> The conversation's identifier on the page, the model name shown in the assistant's model picker, a random identifier for this browser installation, and the extension's version.</Li>
          <Li><B>Decisions.</B> When a message is stopped, or sent without a check because RevSec could not be reached, a record of that decision (which policy, and why) is sent to your organization's RevSec service.</Li>
        </ul>
        <P>The extension does not read other websites, does not record your browsing history, and does not read passwords or sign-in tokens. On grok.com it turns off the page's search suggestions, which would otherwise send what you type to grok.com before you send your message. On github.com it acts only on Copilot's pages and never reads code, issues or pull requests.</P>

        <H2>2. Where it goes</H2>
        <P>Everything above is sent only to the RevSec service your organization uses, at the address your IT team configured. Your organization is the controller of this data; RevSec processes it on your organization's behalf under its agreement with your organization. How long records are kept, and who in your organization can see them, is set by your organization.</P>
        <P>RevSec checks a message with rules and, as part of the RevSec service, with AI analysis run in RevSec's own cloud environment. RevUp AI does not sell this data, does not use it for advertising, and does not use it for any purpose other than checking messages against your organization's policy and keeping your organization's records.</P>

        <H2>3. What is stored in your browser</H2>
        <P>The settings your IT team pushed, a copy of your organization's policy that is refreshed every five minutes, and decisions waiting to be sent while RevSec is unreachable. They stay in this browser's extension storage and are removed with the extension.</P>

        <H2>4. Your choices</H2>
        <P>The extension is managed by your organization. Questions about the policies it applies, or about records your organization keeps, go to your IT or security team. Questions about this policy: <a href="mailto:nipun@revupai.com" style={{ color:C, textDecoration:'none' }}>nipun@revupai.com</a>.</P>

        <H2>5. Changes</H2>
        <P>Changes to this policy are published at this address with a new date.</P>

      </div>
    </div>
    </>
  );
}
